var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { isNil, mergeProps, useClasses, useConsoleLog, useLayoutProps, useProps, useSkeletonLoader, } from '@hawkins/helpers';
import { IconChevronRight } from '@hawkins/icons/lib/iconChevronRight';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { Button } from '../button';
import { Checkbox } from '../checkbox';
import { Heading } from '../heading';
import { usePress } from '../hooks';
import { Text } from '../text';
import { AccordionContext } from './accordion.context';
import { generateItemId } from './accordion.utils';
import { useAccordionSummaryStyles } from './accordionSummary.styles';
var displayName = 'AccordionSummary';
/**
 * `AccordionSummary` is the title panel for an `Accordion`.
 */
export var AccordionSummary = React.forwardRef(function (_a, ref) {
    var _b, _c, _d, _e, _f, _g, _h;
    var _j, _k;
    var children = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _l = useProps(displayName, props), additionalAction = _l.additionalAction, _m = _l.checkbox, checkbox = _m === void 0 ? false : _m, _o = _l.checkboxProps, _p = _o === void 0 ? {} : _o, _q = _p.onClick, onClickCheckboxProp = _q === void 0 ? undefined : _q, checkboxProps = __rest(_p, ["onClick"]), classesProp = _l.classes, className = _l.className, _r = _l.component, Component = _r === void 0 ? 'div' : _r, _s = _l.hideToggleIcon, hideToggleIcon = _s === void 0 ? false : _s, layout = _l.layout, loaderProps = _l.loaderProps, loading = _l.loading, _t = _l.size, size = _t === void 0 ? 'regular' : _t, styleProp = _l.style, subtitle = _l.subtitle, subtitleProps = _l.subtitleProps, title = _l.title, titlePropsProp = _l.titleProps, toggleButtonProps = _l.toggleButtonProps, toggleIcon = _l.toggleIcon, _u = _l.toggleIconPosition, toggleIconPosition = _u === void 0 ? 'right' : _u, _v = _l.toggleWithIconOnly, toggleWithIconOnly = _v === void 0 ? false : _v, restProps = __rest(_l, ["additionalAction", "checkbox", "checkboxProps", "classes", "className", "component", "hideToggleIcon", "layout", "loaderProps", "loading", "size", "style", "subtitle", "subtitleProps", "title", "titleProps", "toggleButtonProps", "toggleIcon", "toggleIconPosition", "toggleWithIconOnly"]);
    var onCheckboxClick = React.useCallback(function (ev) {
        onClickCheckboxProp === null || onClickCheckboxProp === void 0 ? void 0 : onClickCheckboxProp(ev);
        // Prevent a checkbox click from bubbling up to its parent `AccordionSummary`.
        ev.stopPropagation();
    }, [onClickCheckboxProp]);
    var hasSubtitle = !!subtitle;
    var hasTitle = !!title;
    var isLeftAligned = toggleIconPosition === 'left';
    var isCompact = size === 'compact';
    var isLarge = size === 'large';
    useConsoleLog(hideToggleIcon && toggleWithIconOnly, '`props.toggleWithIconOnly` will NOT be enabled if the toggle icon is hidden.', { displayName: displayName, docsPath: 'components-accordion#toggle-customization' });
    useConsoleLog(!hideToggleIcon && isLeftAligned && checkbox, 'A `Checkbox` will NOT be rendered when the toggle icon is left-aligned.', { displayName: displayName, docsPath: 'components-accordion#checkbox' });
    useConsoleLog(!hideToggleIcon && !isLeftAligned && !isNil(additionalAction), '`props.additionalAction` will NOT be rendered when the toggle icon is right-aligned.', { displayName: displayName, docsPath: 'components-accordion#left-aligned' });
    // Context
    var _w = React.useContext(AccordionContext), baseId = _w.baseId, disabled = _w.disabled, expanded = _w.expanded, onChange = _w.onChange, panelId = _w.panelId, setSubtitleId = _w.setSubtitleId, setTitleId = _w.setTitleId;
    var toggleAccordion = React.useCallback(function (ev) {
        onChange(ev, !expanded);
    }, [expanded, onChange]);
    var pressProps = usePress({ disabled: disabled, onPress: toggleAccordion }).pressProps;
    var subtitleId = hasSubtitle ? (_j = subtitleProps === null || subtitleProps === void 0 ? void 0 : subtitleProps.id) !== null && _j !== void 0 ? _j : generateItemId(baseId, 'subtitle') : undefined;
    var titleId = hasTitle ? (_k = titlePropsProp === null || titlePropsProp === void 0 ? void 0 : titlePropsProp.id) !== null && _k !== void 0 ? _k : generateItemId(baseId, 'title') : undefined;
    React.useEffect(function () {
        setSubtitleId(subtitleId);
    }, [setSubtitleId, subtitleId]);
    React.useEffect(function () {
        setTitleId(titleId);
    }, [setTitleId, titleId]);
    // Styles
    var baseClasses = useAccordionSummaryStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, HawkinsGlobalClass.FOCUSABLE, className, classes.root, (_b = {},
        _b[classes.rootAuto] = size === 'auto',
        _b[classes.rootCompact] = isCompact,
        _b[classes.rootDisabled] = disabled,
        _b[classes.rootExpanded] = expanded,
        _b[classes.rootLarge] = isLarge,
        _b[classes.rootToggleable] = !disabled && !toggleWithIconOnly,
        _b[classes.rootWithTitleAndSubtitle] = hasSubtitle,
        _b));
    var skeletonLoadersContainerClassName = clsx(classes.skeletonLoadersContainer, (_c = {},
        _c[classes.skeletonLoadersContainerCompact] = isCompact,
        _c[classes.skeletonLoadersContainerLarge] = isLarge,
        _c[classes.skeletonLoadersContainerLeftAligned] = isLeftAligned,
        _c[classes.skeletonLoadersContainerNoSubtitle] = !hasSubtitle,
        _c[classes.skeletonLoadersContainerRegular] = !isCompact && !isLarge,
        _c[classes.skeletonLoadersContainerToggleIconHidden] = hideToggleIcon,
        _c));
    var subtitleSkeletonLoaderContainerClassName = clsx(classes.subtitleSkeletonLoaderContainer, (_d = {},
        _d[classes.subtitleSkeletonLoaderContainerCompact] = isCompact,
        _d));
    var titleSkeletonLoaderContainerClassName = clsx(classes.titleSkeletonLoaderContainer, (_e = {},
        _e[classes.titleSkeletonLoaderContainerLarge] = isLarge,
        _e));
    var toggleIconButtonClassName = clsx(classes.toggleIconButton, (_f = {},
        _f[classes.toggleIconButtonCompact] = isCompact,
        _f[classes.toggleIconButtonEnabled] = !disabled,
        _f));
    var toggleIconContainerClassName = clsx(classes.toggleIconContainer, (_g = {},
        _g[classes.toggleIconContainerCompact] = isCompact,
        _g[classes.toggleIconContainerRightAligned] = !isLeftAligned,
        _g));
    var toggleIconSkeletonLoaderContainerClassName = clsx(classes.toggleIconSkeletonLoaderContainer, (_h = {},
        _h[classes.toggleIconSkeletonLoaderContainerCompact] = isCompact,
        _h));
    // Content
    var subtitleSkeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ 'aria-hidden': true, height: '100%', variant: 'text', width: '75%' }, loaderProps),
        loading: loading,
        style: style,
    });
    var titleSkeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ height: '100%', variant: 'text', width: '100%' }, loaderProps),
        loading: loading,
        style: style,
    });
    var toggleIconSkeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ 'aria-hidden': true, height: '100%', variant: 'circle', width: '100%' }, loaderProps),
        loading: loading,
        style: style,
    });
    if (subtitleSkeletonLoaderNode || titleSkeletonLoaderNode || toggleIconSkeletonLoaderNode) {
        return (_jsxs("div", __assign({ className: skeletonLoadersContainerClassName }, { children: [_jsx("div", __assign({ className: titleSkeletonLoaderContainerClassName }, { children: titleSkeletonLoaderNode })), hasSubtitle && _jsx("div", __assign({ className: subtitleSkeletonLoaderContainerClassName }, { children: subtitleSkeletonLoaderNode })), !hideToggleIcon && (_jsx("div", __assign({ className: toggleIconSkeletonLoaderContainerClassName }, { children: toggleIconSkeletonLoaderNode })))] })));
    }
    var titleProps = __assign({ children: title, colorVariant: disabled ? 'disabled' : 'default', component: 'div', id: titleId, loading: loading }, titlePropsProp);
    var toggleAriaAttributes = {
        'aria-controls': panelId,
        'aria-expanded': expanded !== null && expanded !== void 0 ? expanded : undefined,
    };
    var defaultToggleIconNode = isNil(toggleIcon) ? (_jsx(IconChevronRight, { "aria-hidden": true, className: classes.toggleIcon, size: isCompact ? 'small' : undefined })) : React.isValidElement(toggleIcon) ? (React.cloneElement(toggleIcon, { className: toggleIcon.props.className || classes.toggleIcon })) : (toggleIcon);
    var toggleIconNode = (_jsx("div", __assign({ className: toggleIconContainerClassName }, { children: toggleWithIconOnly ? (_jsx(Button, __assign({ "aria-describedby": subtitleId, "aria-labelledby": titleId, ariaDisableOnly: true, className: toggleIconButtonClassName, compact: isCompact, disabled: disabled, loading: loading, onClick: toggleAccordion, variant: "tertiary" }, toggleAriaAttributes, toggleButtonProps, { children: defaultToggleIconNode }))) : (defaultToggleIconNode) })));
    return (_jsxs(Component, __assign({ className: rootClassName, ref: ref, style: style }, (toggleWithIconOnly
        ? restProps
        : __assign(__assign({ 'aria-disabled': disabled || undefined, role: 'button', tabIndex: 0 }, toggleAriaAttributes), mergeProps(restProps, pressProps))), { children: [!hideToggleIcon && isLeftAligned
                ? toggleIconNode
                : checkbox && _jsx(Checkbox, __assign({ disabled: disabled, onClick: onCheckboxClick }, checkboxProps)), (hasTitle || hasSubtitle) && (_jsxs("div", __assign({ className: classes.content }, { children: [hasTitle &&
                        (isLarge ? (_jsx(Heading, __assign({ level: 1 }, titleProps))) : (_jsx(Text, __assign({ bold: true, variant: isCompact ? 'label' : 'body' }, titleProps)))), hasSubtitle && (_jsx(Text, __assign({ colorVariant: disabled ? 'disabled' : 'faded', component: "div", id: subtitleId, loading: loading, variant: isCompact ? 'label' : 'body' }, subtitleProps, { children: subtitle })))] }))), children, !hideToggleIcon && !isLeftAligned
                ? toggleIconNode
                : additionalAction && _jsx("div", __assign({ className: classes.additionalActionContainer }, { children: additionalAction }))] })));
});
AccordionSummary.displayName = displayName;
